function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open')
    );
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;

    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];

  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) {
        currentFocusedElement.classList.remove('focused');
      }

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"pauseVideo","args":""}',
      '*'
    );
  });

  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });

  document.querySelectorAll('video').forEach((video) => video.pause());

  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.input.addEventListener(
      'change',
      this.onInputChange.bind(this)
    );

    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener(
        'click',
        this.onButtonClick.bind(this)
      )
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();

    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === 'minus'
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);

    if (this.input.min) {
      const buttonMinus = this.querySelector(
        ".quantity__button[name='minus']"
      );

      buttonMinus.classList.toggle(
        'disabled',
        parseInt(value) <= parseInt(this.input.min)
      );
    }

    if (this.input.max) {
      const max = parseInt(this.input.max);

      const buttonPlus = this.querySelector(
        ".quantity__button[name='plus']"
      );

      buttonPlus.classList.toggle(
        'disabled',
        value >= max
      );
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;

  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;

  return function (...args) {
    const now = new Date().getTime();

    if (now - lastCall < delay) {
      return;
    }

    lastCall = now;

    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */

if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];

    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};

  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');

  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');

    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);

    form.appendChild(hiddenField);
  }

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);

  this.provinceContainer = document.getElementById(
    options['hideElement'] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    'change',
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');

    Shopify.setSelectorByValue(
      this.countryEl,
      value
    );

    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');

    if (
      value &&
      this.provinceEl.options.length > 0
    ) {
      Shopify.setSelectorByValue(
        this.provinceEl,
        value
      );
    }
  },

  countryHandler: function (e) {
    var opt =
      this.countryEl.options[
        this.countryEl.selectedIndex
      ];

    var raw = opt.getAttribute(
      'data-provinces'
    );

    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);

    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display =
        'none';
    } else {
      for (
        var i = 0;
        i < provinces.length;
        i++
      ) {
        var opt =
          document.createElement('option');

        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];

        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display =
        '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (
      var i = 0,
        count = values.length;
      i < values.length;
      i++
    ) {
      var opt =
        document.createElement('option');

      opt.value = values[i];
      opt.innerHTML = values[i];

      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle =
      this.querySelector('details');

    this.addEventListener(
      'keyup',
      this.onKeyUp.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(
      (summary) =>
        summary.addEventListener(
          'click',
          this.onSummaryClick.bind(this)
        )
    );

    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) =>
      button.addEventListener(
        'click',
        this.onCloseButtonClick.bind(this)
      )
    );
  }

  onKeyUp(event) {
    if (
      event.code.toUpperCase() !==
      'ESCAPE'
    )
      return;

    const openDetailsElement =
      event.target.closest('details[open]');

    if (!openDetailsElement) return;

    openDetailsElement ===
    this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector(
            'summary'
          )
        )
      : this.closeSubmenu(
          openDetailsElement
        );
  }

  onSummaryClick(event) {
    const summaryElement =
      event.currentTarget;

    const detailsElement =
      summaryElement.parentNode;

    const parentMenuElement =
      detailsElement.closest('.has-submenu');

    const isOpen =
      detailsElement.hasAttribute('open');

    const reducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector(
          'button'
        )
      );

      summaryElement.nextElementSibling.removeEventListener(
        'transitionend',
        addTrapFocus
      );
    }

    if (
      detailsElement ===
      this.mainDetailsToggle
    ) {
      if (isOpen) {
        event.preventDefault();
      }

      isOpen
        ? this.closeMenuDrawer(
            event,
            summaryElement
          )
        : this.openMenuDrawer(
            summaryElement
          );

      if (
        window.matchMedia(
          '(max-width: 990px)'
        )
      ) {
        document.documentElement.style.setProperty(
          '--viewport-height',
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add(
          'menu-opening'
        );

        summaryElement.setAttribute(
          'aria-expanded',
          true
        );

        parentMenuElement &&
          parentMenuElement.classList.add(
            'submenu-open'
          );

        !reducedMotion ||
        reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              'transitionend',
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add(
        'menu-opening'
      );
    });

    summaryElement.setAttribute(
      'aria-expanded',
      true
    );

    trapFocus(
      this.mainDetailsToggle,
      summaryElement
    );

    document.body.classList.add(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
  }

  closeMenuDrawer(
    event,
    elementToFocus = false
  ) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove(
      'menu-opening'
    );

    this.mainDetailsToggle
      .querySelectorAll('details')
      .forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove(
          'menu-opening'
        );
      });

    this.mainDetailsToggle
      .querySelectorAll('.submenu-open')
      .forEach((submenu) => {
        submenu.classList.remove(
          'submenu-open'
        );
      });

    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );

    removeTrapFocus(elementToFocus);

    this.closeAnimation(
      this.mainDetailsToggle
    );

    if (
      event instanceof KeyboardEvent
    ) {
      elementToFocus?.setAttribute(
        'aria-expanded',
        false
      );
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute(
          'open'
        ) &&
        !this.mainDetailsToggle.contains(
          document.activeElement
        )
      ) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(event) {
    const detailsElement =
      event.currentTarget.closest(
        'details'
      );

    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement =
      detailsElement.closest(
        '.submenu-open'
      );

    parentMenuElement &&
      parentMenuElement.classList.remove(
        'submenu-open'
      );

    detailsElement.classList.remove(
      'menu-opening'
    );

    detailsElement
      .querySelector('summary')
      .setAttribute(
        'aria-expanded',
        false
      );

    removeTrapFocus(
      detailsElement.querySelector(
        'summary'
      )
    );

    this.closeAnimation(
      detailsElement
    );
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime =
        time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(
          handleAnimation
        );
      } else {
        detailsElement.removeAttribute(
          'open'
        );

        if (
          detailsElement.closest(
            'details[open]'
          )
        ) {
          trapFocus(
            detailsElement.closest(
              'details[open]'
            ),
            detailsElement.querySelector(
              'summary'
            )
          );
        }
      }
    };

    window.requestAnimationFrame(
      handleAnimation
    );
  }
}

customElements.define(
  'menu-drawer',
  MenuDrawer
);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header =
      this.header ||
      document.querySelector(
        '.section-header'
      );

    this.borderOffset =
      this.borderOffset ||
      this.closest(
        '.header-wrapper'
      ).classList.contains(
        'header-wrapper--border-bottom'
      )
        ? 1
        : 0;

    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(
        this.header.getBoundingClientRect()
          .bottom -
          this.borderOffset
      )}px`
    );

    this.header.classList.add(
      'menu-open'
    );

    setTimeout(() => {
      this.mainDetailsToggle.classList.add(
        'menu-opening'
      );
    });

    summaryElement.setAttribute(
      'aria-expanded',
      true
    );

    window.addEventListener(
      'resize',
      this.onResize
    );

    trapFocus(
      this.mainDetailsToggle,
      summaryElement
    );

    document.body.classList.add(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
  }

  closeMenuDrawer(
    event,
    elementToFocus
  ) {
    if (!elementToFocus) return;

    super.closeMenuDrawer(
      event,
      elementToFocus
    );

    this.header.classList.remove(
      'menu-open'
    );

    window.removeEventListener(
      'resize',
      this.onResize
    );
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(
          this.header.getBoundingClientRect()
            .bottom -
            this.borderOffset
        )}px`
      );

    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );
  };
}

customElements.define(
  'header-drawer',
  HeaderDrawer
);

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.querySelector(
      '[id^="ModalClose-"]'
    ).addEventListener(
      'click',
      this.hide.bind(this, false)
    );

    this.addEventListener(
      'keyup',
      (event) => {
        if (
          event.code.toUpperCase() ===
          'ESCAPE'
        ) {
          this.hide();
        }
      }
    );

    if (
      this.classList.contains(
        'media-modal'
      )
    ) {
      this.addEventListener(
        'pointerup',
        (event) => {
          if (
            event.pointerType ===
              'mouse' &&
            !event.target.closest(
              'deferred-media, product-model'
            )
          ) {
            this.hide();
          }
        }
      );
    } else {
      this.addEventListener(
        'click',
        (event) => {
          if (event.target === this) {
            this.hide();
          }
        }
      );
    }
  }

  connectedCallback() {
    if (this.moved) return;

    this.moved = true;

    this.dataset.section =
      this.closest(
        '.shopify-section'
      ).id.replace(
        'shopify-section-',
        ''
      );

    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;

    const popup =
      this.querySelector(
        '.template-popup'
      );

    document.body.classList.add(
      'overflow-hidden'
    );

    this.setAttribute(
      'open',
      ''
    );

    if (popup) {
      popup.loadContent();
    }

    trapFocus(
      this,
      this.querySelector(
        '[role="dialog"]'
      )
    );

    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove(
      'overflow-hidden'
    );

    document.body.dispatchEvent(
      new CustomEvent('modalClosed')
    );

    this.removeAttribute('open');

    removeTrapFocus(
      this.openedBy
    );

    window.pauseAllMedia();
  }
}

customElements.define(
  'modal-dialog',
  ModalDialog
);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button =
      this.querySelector('button');

    if (!button) return;

    button.addEventListener(
      'click',
      () => {
        const modal =
          document.querySelector(
            this.getAttribute(
              'data-modal'
            )
          );

        if (modal) {
          modal.show(button);
        }
      }
    );
  }
}

customElements.define(
  'modal-opener',
  ModalOpener
);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    const poster =
      this.querySelector(
        '[id^="Deferred-Poster-"]'
      );

    if (!poster) return;

    poster.addEventListener(
      'click',
      this.loadContent.bind(this)
    );
  }

  loadContent(focus = true) {
    window.pauseAllMedia();

    if (
      !this.getAttribute('loaded')
    ) {
      const content =
        document.createElement(
          'div'
        );

      content.appendChild(
        this.querySelector(
          'template'
        ).content.firstElementChild.cloneNode(
          true
        )
      );

      this.setAttribute(
        'loaded',
        true
      );

      const deferredElement =
        this.appendChild(
          content.querySelector(
            'video, model-viewer, iframe'
          )
        );

      if (focus) {
        deferredElement.focus();
      }

      if (
        deferredElement.nodeName ==
          'VIDEO' &&
        deferredElement.getAttribute(
          'autoplay'
        )
      ) {
        deferredElement.play();
      }

      const formerStyle =
        deferredElement.getAttribute(
          'style'
        );

      deferredElement.setAttribute(
        'style',
        'display: block;'
      );

      window.setTimeout(() => {
        deferredElement.setAttribute(
          'style',
          formerStyle
        );
      }, 0);
    }
  }
}

customElements.define(
  'deferred-media',
  DeferredMedia
);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider =
      this.querySelector(
        '[id^="Slider-"]'
      );

    this.sliderItems =
      this.querySelectorAll(
        '[id^="Slide-"]'
      );

    this.enableSliderLooping = false;

    this.currentPageElement =
      this.querySelector(
        '.slider-counter--current'
      );

    this.pageTotalElement =
      this.querySelector(
        '.slider-counter--total'
      );

    this.prevButton =
      this.querySelector(
        'button[name="previous"]'
      );

    this.nextButton =
      this.querySelector(
        'button[name="next"]'
      );

    if (
      !this.slider ||
      !this.nextButton
    )
      return;

    this.initPages();

    const resizeObserver =
      new ResizeObserver(
        (entries) =>
          this.initPages()
      );

    resizeObserver.observe(
      this.slider
    );

    this.slider.addEventListener(
      'scroll',
      this.update.bind(this)
    );

    this.prevButton.addEventListener(
      'click',
      this.onButtonClick.bind(this)
    );

    this.nextButton.addEventListener(
      'click',
      this.onButtonClick.bind(this)
    );
  }

  initPages() {
    this.sliderItemsToShow =
      Array.from(
        this.sliderItems
      ).filter(
        (element) =>
          element.clientWidth > 0
      );

    if (
      this.sliderItemsToShow.length <
      2
    )
      return;

    this.sliderItemOffset =
      this.sliderItemsToShow[1]
        .offsetLeft -
      this.sliderItemsToShow[0]
        .offsetLeft;

    this.slidesPerPage = Math.floor(
      (
        this.slider.clientWidth -
        this.sliderItemsToShow[0]
          .offsetLeft
      ) /
        this.sliderItemOffset
    );

    this.totalPages =
      this.sliderItemsToShow.length -
      this.slidesPerPage +
      1;

    this.update();
  }

  resetPages() {
    this.sliderItems =
      this.querySelectorAll(
        '[id^="Slide-"]'
      );

    this.initPages();
  }

  update() {
    if (
      !this.slider ||
      !this.nextButton
    )
      return;

    const previousPage =
      this.currentPage;

    this.currentPage =
      Math.round(
        this.slider.scrollLeft /
          this.sliderItemOffset
      ) + 1;

    if (
      this.currentPageElement &&
      this.pageTotalElement
    ) {
      this.currentPageElement.textContent =
        this.currentPage;

      this.pageTotalElement.textContent =
        this.totalPages;
    }

    if (
      this.currentPage !=
      previousPage
    ) {
      this.dispatchEvent(
        new CustomEvent(
          'slideChanged',
          {
            detail: {
              currentPage:
                this.currentPage,
              currentElement:
                this.sliderItemsToShow[
                  this.currentPage - 1
                ],
            },
          }
        )
      );
    }

    if (
      this.enableSliderLooping
    )
      return;

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[0]
      ) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute(
        'disabled',
        'disabled'
      );
    } else {
      this.prevButton.removeAttribute(
        'disabled'
      );
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[
          this.sliderItemsToShow.length -
            1
        ]
      )
    ) {
      this.nextButton.setAttribute(
        'disabled',
        'disabled'
      );
    } else {
      this.nextButton.removeAttribute(
        'disabled'
      );
    }
  }

  isSlideVisible(
    element,
    offset = 0
  ) {
    const lastVisibleSlide =
      this.slider.clientWidth +
      this.slider.scrollLeft -
      offset;

    return (
      element.offsetLeft +
        element.clientWidth <=
        lastVisibleSlide &&
      element.offsetLeft >=
        this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const step =
      event.currentTarget.dataset.step ||
      1;

    this.slideScrollPosition =
      event.currentTarget.name ===
      'next'
        ? this.slider.scrollLeft +
          step *
            this.sliderItemOffset
        : this.slider.scrollLeft -
          step *
            this.sliderItemOffset;

    this.setSlidePosition(
      this.slideScrollPosition
    );
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define(
  'slider-component',
  SliderComponent
);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();

    this.sliderControlWrapper =
      this.querySelector(
        '.slider-buttons'
      );

    this.enableSliderLooping = true;

    if (
      !this.sliderControlWrapper
    )
      return;

    this.sliderFirstItemNode =
      this.slider.querySelector(
        '.slideshow__slide'
      );

    if (
      this.sliderItemsToShow.length >
      0
    ) {
      this.currentPage = 1;
    }

    this.announcementBarSlider =
      this.querySelector(
        '.announcement-bar-slider'
      );

    this.announcerBarAnimationDelay =
      this.announcementBarSlider
        ? 250
        : 0;

    this.sliderControlLinksArray =
      Array.from(
        this.sliderControlWrapper.querySelectorAll(
          '.slider-counter__link'
        )
      );

    this.sliderControlLinksArray.forEach(
      (link) =>
        link.addEventListener(
          'click',
          this.linkToSlide.bind(this)
        )
    );

    this.slider.addEventListener(
      'scroll',
      this.setSlideVisibility.bind(
        this
      )
    );

    this.setSlideVisibility();

    if (
      this.announcementBarSlider
    ) {
      this.announcementBarArrowButtonWasClicked =
        false;

      this.reducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        );

      this.reducedMotion.addEventListener(
        'change',
        () => {
          if (
            this.slider.getAttribute(
              'data-autoplay'
            ) === 'true'
          ) {
            this.setAutoPlay();
          }
        }
      );

      [
        this.prevButton,
        this.nextButton,
      ].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked =
              true;
          },
          { once: true }
        );
      });
    }

    if (
      this.slider.getAttribute(
        'data-autoplay'
      ) === 'true'
    ) {
      this.setAutoPlay();
    }
  }

  setAutoPlay() {
    this.autoplaySpeed =
      this.slider.dataset.speed *
      1000;

    this.addEventListener(
      'mouseover',
      this.focusInHandling.bind(
        this
      )
    );

    this.addEventListener(
      'mouseleave',
      this.focusOutHandling.bind(
        this
      )
    );

    this.addEventListener(
      'focusin',
      this.focusInHandling.bind(
        this
      )
    );

    this.addEventListener(
      'focusout',
      this.focusOutHandling.bind(
        this
      )
    );

    if (
      this.querySelector(
        '.slideshow__autoplay'
      )
    ) {
      this.sliderAutoplayButton =
        this.querySelector(
          '.slideshow__autoplay'
        );

      this.sliderAutoplayButton.addEventListener(
        'click',
        this.autoPlayToggle.bind(
          this
        )
      );

      this.autoplayButtonIsSetToPlay =
        true;

      this.play();
    } else {
      this.reducedMotion.matches ||
      this
        .announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);

    this.wasClicked = true;

    const isFirstSlide =
      this.currentPage === 1;

    const isLastSlide =
      this.currentPage ===
      this.sliderItemsToShow.length;

    if (
      !isFirstSlide &&
      !isLastSlide
    ) {
      this.applyAnimationToAnnouncementBar(
        event.currentTarget.name
      );

      return;
    }

    if (
      isFirstSlide &&
      event.currentTarget.name ===
        'previous'
    ) {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode
          .clientWidth *
          this.sliderItemsToShow
            .length;
    } else if (
      isLastSlide &&
      event.currentTarget.name ===
        'next'
    ) {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(
      this.slideScrollPosition
    );

    this.applyAnimationToAnnouncementBar(
      event.currentTarget.name
    );
  }

  setSlidePosition(position) {
    if (
      this.setPositionTimeout
    ) {
      clearTimeout(
        this.setPositionTimeout
      );
    }

    this.setPositionTimeout =
      setTimeout(() => {
        this.slider.scrollTo({
          left: position,
        });
      }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();

    this.sliderControlButtons =
      this.querySelectorAll(
        '.slider-counter__link'
      );

    this.prevButton.removeAttribute(
      'disabled'
    );

    if (
      !this.sliderControlButtons.length
    )
      return;

    this.sliderControlButtons.forEach(
      (link) => {
        link.classList.remove(
          'slider-counter__link--active'
        );

        link.removeAttribute(
          'aria-current'
        );
      }
    );

    this.sliderControlButtons[
      this.currentPage - 1
    ].classList.add(
      'slider-counter__link--active'
    );

    this.sliderControlButtons[
      this.currentPage - 1
    ].setAttribute(
      'aria-current',
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(
      this.autoplayButtonIsSetToPlay
    );

    this.autoplayButtonIsSetToPlay
      ? this.pause()
      : this.play();

    this.autoplayButtonIsSetToPlay =
      !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (
      this.sliderAutoplayButton
    ) {
      const focusedOnAutoplayButton =
        event.target ===
          this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(
          event.target
        );

      if (
        !this.autoplayButtonIsSetToPlay ||
        focusedOnAutoplayButton
      )
        return;

      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this
        .announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (
      this.sliderAutoplayButton
    ) {
      const focusedOnAutoplayButton =
        event.target ===
          this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(
          event.target
        );

      if (
        focusedOnAutoplayButton &&
        this.autoplayButtonIsSetToPlay
      ) {
        this.play();
      } else if (
        this.autoplayButtonIsSetToPlay
      ) {
        this.pause();
      }
    } else if (
      this.announcementBarSlider.contains(
        event.target
      )
    ) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute(
      'aria-live',
      'off'
    );

    clearInterval(this.autoplay);

    this.autoplay = setInterval(
      this.autoRotateSlides.bind(
        this
      ),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute(
      'aria-live',
      'polite'
    );

    clearInterval(this.autoplay);
  }

  togglePlayButtonState(
    pauseAutoplay
  ) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add(
        'slideshow__autoplay--paused'
      );

      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings
          .playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove(
        'slideshow__autoplay--paused'
      );

      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window.accessibilityStrings
          .pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage ===
      this.sliderItems.length
        ? 0
        : this.slider.scrollLeft +
          this.sliderItemOffset;

    this.setSlidePosition(
      slideScrollPosition
    );

    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach(
      (item, index) => {
        const linkElements =
          item.querySelectorAll('a');

        if (
          index ===
          this.currentPage - 1
        ) {
          if (
            linkElements.length
          ) {
            linkElements.forEach(
              (button) => {
                button.removeAttribute(
                  'tabindex'
                );
              }
            );
          }

          item.setAttribute(
            'aria-hidden',
            'false'
          );

          item.removeAttribute(
            'tabindex'
          );
        } else {
          if (
            linkElements.length
          ) {
            linkElements.forEach(
              (button) => {
                button.setAttribute(
                  'tabindex',
                  '-1'
                );
              }
            );
          }

          item.setAttribute(
            'aria-hidden',
            'true'
          );

          item.setAttribute(
            'tabindex',
            '-1'
          );
        }
      }
    );

    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(
    button = 'next'
  ) {
    if (
      !this.announcementBarSlider
    )
      return;

    const itemsCount =
      this.sliderItems.length;

    const increment =
      button === 'next'
        ? 1
        : -1;

    const currentIndex =
      this.currentPage - 1;

    let nextIndex =
      (currentIndex + increment) %
      itemsCount;

    nextIndex =
      nextIndex === -1
        ? itemsCount - 1
        : nextIndex;

    const nextSlide =
      this.sliderItems[nextIndex];

    const currentSlide =
      this.sliderItems[currentIndex];

    const animationClassIn =
      'announcement-bar-slider--fade-in';

    const animationClassOut =
      'announcement-bar-slider--fade-out';

    const isFirstSlide =
      currentIndex === 0;

    const isLastSlide =
      currentIndex ===
      itemsCount - 1;

    const shouldMoveNext =
      (button === 'next' &&
        !isLastSlide) ||
      (button === 'previous' &&
        isFirstSlide);

    const direction =
      shouldMoveNext
        ? 'next'
        : 'previous';

    currentSlide.classList.add(
      `${animationClassOut}-${direction}`
    );

    nextSlide.classList.add(
      `${animationClassIn}-${direction}`
    );

    setTimeout(() => {
      currentSlide.classList.remove(
        `${animationClassOut}-${direction}`
      );

      nextSlide.classList.remove(
        `${animationClassIn}-${direction}`
      );
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();

    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode
        .clientWidth *
        (
          this.sliderControlLinksArray.indexOf(
            event.currentTarget
          ) +
          1 -
          this.currentPage
        );

    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define(
  'slideshow-component',
  SlideshowComponent
);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener(
      'change',
      (event) => {
        const target =
          this.getInputForEventTarget(
            event.target
          );

        this.updateSelectionMetadata(
          event
        );

        this.dispatchProductSelectEvent();

        publish(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          {
            data: {
              event,
              target,
              selectedOptionValues:
                this.selectedOptionValues,
            },
          }
        );
      }
    );
  }

  getAllSelectedOptions() {
    const options = [];

    this.querySelectorAll(
      'fieldset, .product-form__input--dropdown'
    ).forEach((group) => {
      const checked =
        group.querySelector(
          'input:checked'
        ) ||
        group.querySelector(
          'select option[selected]'
        );

      if (checked) {
        options.push({
          name:
            checked.dataset
              .optionName || '',
          value: checked.value,
        });
      }
    });

    return options;
  }

  dispatchProductSelectEvent() {
    const {
      ProductSelectEvent,
    } = window.StandardEvents || {};

    if (!ProductSelectEvent)
      return;

    const deferred =
      ProductSelectEvent.createPromise();

    this.pendingSelectPromise =
      deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title:
            this.dataset.productTitle,
          handle:
            this.dataset.productHandle,
        },
        selectedOptions:
          this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred =
      this.pendingSelectPromise;

    this.pendingSelectPromise =
      null;

    return deferred;
  }

  resolvePendingSelectPromise(
    variant,
    sourceVariantSelects = this
  ) {
    const deferred =
      this.takePendingSelectPromise();

    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale:
            variant.available,

          price: {
            amount:
              sourceVariantSelects?.dataset
                .selectedPriceAmount,

            currencyCode:
              sourceVariantSelects?.dataset
                .currencyCode,
          },

          selectedOptions:
            this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({
        variant: null,
      });
    }
  }

  rejectPendingSelectPromise(
    error
  ) {
    this.takePendingSelectPromise()?.reject(
      error
    );
  }

  updateSelectionMetadata({
    target,
  }) {
    const {
      value,
      tagName,
    } = target;

    if (
      tagName === 'SELECT' &&
      target.selectedOptions.length
    ) {
      Array.from(
        target.options
      )
        .find((option) =>
          option.getAttribute(
            'selected'
          )
        )
        .removeAttribute(
          'selected'
        );

      target.selectedOptions[0].setAttribute(
        'selected',
        'selected'
      );

      const swatchValue =
        target.selectedOptions[0]
          .dataset
          .optionSwatchValue;

      const selectedDropdownSwatchValue =
        target
          .closest(
            '.product-form__input'
          )
          .querySelector(
            '[data-selected-value] > .swatch'
          );

      if (
        !selectedDropdownSwatchValue
      )
        return;

      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          swatchValue
        );

        selectedDropdownSwatchValue.classList.remove(
          'swatch--unavailable'
        );
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          'unset'
        );

        selectedDropdownSwatchValue.classList.add(
          'swatch--unavailable'
        );
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0]
          .dataset
          .optionSwatchFocalPoint ||
          'unset'
      );
    } else if (
      tagName === 'INPUT' &&
      target.type === 'radio'
    ) {
      const selectedSwatchValue =
        target
          .closest(
            `.product-form__input`
          )
          .querySelector(
            '[data-selected-value]'
          );

      if (selectedSwatchValue) {
        selectedSwatchValue.innerHTML =
          value;
      }
    }
  }

  getInputForEventTarget(
    target
  ) {
    return target.tagName ===
      'SELECT'
      ? target.selectedOptions[0]
      : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked'
      )
    ).map(
      ({ dataset }) =>
        dataset.optionValueId
    );
  }
}

customElements.define(
  'variant-selects',
  VariantSelects
);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(
      this.dataset.productId
    );
  }

  initializeRecommendations(
    productId
  ) {
    this.observer?.unobserve(
      this
    );

    this.observer =
      new IntersectionObserver(
        (
          entries,
          observer
        ) => {
          if (
            !entries[0]
              .isIntersecting
          )
            return;

          observer.unobserve(this);

          this.loadRecommendations(
            productId
          );
        },
        {
          rootMargin:
            '0px 0px 400px 0px',
        }
      );

    this.observer.observe(this);
  }

  loadRecommendations(
    productId
  ) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) =>
        response.text()
      )
      .then((text) => {
        const html =
          document.createElement(
            'div'
          );

        html.innerHTML = text;

        const recommendations =
          html.querySelector(
            'product-recommendations'
          );

        if (
          recommendations?.innerHTML.trim()
            .length
        ) {
          this.innerHTML =
            recommendations.innerHTML;
        }

        if (
          !this.querySelector(
            'slideshow-component'
          ) &&
          this.classList.contains(
            'complementary-products'
          )
        ) {
          this.remove();
        }

        if (
          html.querySelector(
            '.grid__item'
          )
        ) {
          this.classList.add(
            'product-recommendations--loaded'
          );
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define(
  'product-recommendations',
  ProductRecommendations
);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon =
      this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener(
      'storefront:signincompleted',
      this.handleStorefrontSignInCompleted.bind(
        this
      )
    );
  }

  handleStorefrontSignInCompleted(
    event
  ) {
    if (
      event?.detail?.avatar
    ) {
      this.icon?.replaceWith(
        event.detail.avatar.cloneNode()
      );
    }
  }
}

customElements.define(
  'account-icon',
  AccountIcon
);

class CartPerformance {
  static #metric_prefix =
    'cart-performance';

  static createStartingMarker(
    benchmarkName
  ) {
    const metricName =
      `${CartPerformance.#metric_prefix}:${benchmarkName}`;

    return performance.mark(
      `${metricName}:start`
    );
  }

  static measureFromEvent(
    benchmarkName,
    event
  ) {
    const metricName =
      `${CartPerformance.#metric_prefix}:${benchmarkName}`;

    const startMarker =
      performance.mark(
        `${metricName}:start`,
        {
          startTime:
            event.timeStamp,
        }
      );

    const endMarker =
      performance.mark(
        `${metricName}:end`
      );

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(
    benchmarkName,
    startMarker
  ) {
    const metricName =
      `${CartPerformance.#metric_prefix}:${benchmarkName}`;

    const endMarker =
      performance.mark(
        `${metricName}:end`
      );

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(
    benchmarkName,
    callback
  ) {
    const metricName =
      `${CartPerformance.#metric_prefix}:${benchmarkName}`;

    const startMarker =
      performance.mark(
        `${metricName}:start`
      );

    callback();

    const endMarker =
      performance.mark(
        `${metricName}:end`
      );

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/*
 * =========================================================
 * COMPLETE THE LOOK - ADD TO CART BUTTON COLOR
 * =========================================================
 */

document.addEventListener(
  'DOMContentLoaded',
  function () {
    const addToCartButton =
      document.querySelector(
        'product-form .product-form__submit'
      );

    if (!addToCartButton) return;

    function updateAddToCartColor() {
      const selectedColorInput =
        document.querySelector(
          'variant-selects input[data-option-name="Color"]:checked'
        );

      if (!selectedColorInput) {
        console.log(
          'Selected Color input not found'
        );

        return;
      }

      const selectedColor =
        selectedColorInput.value.trim();

      console.log(
        'Selected Color:',
        selectedColor
      );

      const globoSwatch =
        document.querySelector(
          `.globo-swatch-product-detail .value li[data-value="${CSS.escape(
            selectedColor
          )}"]`
        );

      if (!globoSwatch) {
        console.log(
          'Globo swatch not found:',
          selectedColor
        );

        return;
      }

      const swatchColor =
        getComputedStyle(
          globoSwatch
        ).backgroundColor;

      console.log(
        'Globo Swatch Color:',
        swatchColor
      );

      if (
        !swatchColor ||
        swatchColor ===
          'rgba(0, 0, 0, 0)' ||
        swatchColor ===
          'transparent'
      ) {
        return;
      }

      addToCartButton.style.setProperty(
        'background-color',
        swatchColor,
        'important'
      );
    }

    setTimeout(
      updateAddToCartColor,
      300
    );

    setTimeout(
      updateAddToCartColor,
      800
    );

    setTimeout(
      updateAddToCartColor,
      1500
    );

    document.addEventListener(
      'change',
      function (event) {
        if (
          event.target.matches(
            'variant-selects input[data-option-name="Color"]'
          )
        ) {
          setTimeout(
            updateAddToCartColor,
            100
          );

          setTimeout(
            updateAddToCartColor,
            300
          );
        }
      }
    );
  }
);

/*
 * =========================================================
 * COMPLETE THE LOOK - COLOR SELECTION
 * =========================================================
 */

document.addEventListener(
  'click',
  function (event) {
    const colorButton =
      event.target.closest(
        '.ctl-color-option'
      );

    if (!colorButton) return;

    const product =
      colorButton.closest(
        '.ctl-product'
      );

    if (!product) return;

    const variantsElement =
      product.querySelector(
        '.ctl-variants-data'
      );

    if (!variantsElement) return;

    let variants;

    try {
      variants = JSON.parse(
        variantsElement.textContent
      );
    } catch (error) {
      console.error(
        'CTL variants JSON error:',
        error
      );

      return;
    }

    const color =
      colorButton.dataset.color;

    const colorButtons =
      product.querySelectorAll(
        '.ctl-color-option'
      );

    colorButtons.forEach(
      function (button) {
        button.classList.remove(
          'is-selected'
        );
      }
    );

    colorButton.classList.add(
      'is-selected'
    );

    const colorLabel =
      product.querySelector(
        '.ctl-selected-color'
      );

    if (colorLabel) {
      colorLabel.textContent =
        color;
    }

    const firstVariant =
      variants[0];

    if (
      !firstVariant ||
      !firstVariant.options
    )
      return;

    let colorIndex = -1;

    firstVariant.options.forEach(
      function (
        option,
        index
      ) {
        const matchingColor =
          Array.from(
            colorButtons
          ).some(function (button) {
            return (
              button.dataset.color ===
              option
            );
          });

        if (matchingColor) {
          colorIndex = index;
        }
      }
    );

    if (colorIndex === -1)
      return;

    const selectedSizeButton =
      product.querySelector(
        '.ctl-size-option.is-selected'
      );

    const selectedSize =
      selectedSizeButton
        ? selectedSizeButton.dataset
            .size
        : null;

    let matchingVariant =
      null;

    variants.forEach(
      function (variant) {
        if (!variant.options)
          return;

        const sameColor =
          variant.options[
            colorIndex
          ] === color;

        const sameSize =
          !selectedSize ||
          variant.options.includes(
            selectedSize
          );

        if (
          sameColor &&
          sameSize &&
          variant.available
        ) {
          matchingVariant =
            variant;
        }
      }
    );

    if (!matchingVariant) {
      matchingVariant =
        variants.find(
          function (variant) {
            return (
              variant.available &&
              variant.options &&
              variant.options[
                colorIndex
              ] === color
            );
          }
        );
    }

    if (!matchingVariant) {
      console.log(
        'No available variant found for:',
        color
      );

      return;
    }

    const image =
      product.querySelector(
        '.ctl-product__image'
      );

    if (
      image &&
      matchingVariant.featuredImage
    ) {
      image.style.opacity = '0';

      setTimeout(function () {
        image.src =
          matchingVariant.featuredImage;

        image.style.opacity = '1';
      }, 150);
    }

    const price =
      product.querySelector(
        '.ctl-product__price-current'
      );

    if (
      price &&
      matchingVariant.priceFormatted
    ) {
      price.textContent =
        matchingVariant.priceFormatted;
    }

    product.dataset.selectedVariant =
      matchingVariant.id;

    const sizeButtons =
      product.querySelectorAll(
        '.ctl-size-option'
      );

    sizeButtons.forEach(
      function (sizeButton) {
        const size =
          sizeButton.dataset.size;

        const sizeVariant =
          variants.find(
            function (variant) {
              return (
                variant.available &&
                variant.options &&
                variant.options[
                  colorIndex
                ] === color &&
                variant.options.includes(
                  size
                )
              );
            }
          );

        if (sizeVariant) {
          sizeButton.disabled =
            false;

          sizeButton.classList.remove(
            'is-disabled'
          );
        } else {
          sizeButton.disabled =
            true;

          sizeButton.classList.add(
            'is-disabled'
          );
        }
      }
    );
  }
);

/*
 * =========================================================
 * COMPLETE THE LOOK - SIZE SELECTION
 * =========================================================
 */

document.addEventListener(
  'click',
  function (event) {
    const sizeButton =
      event.target.closest(
        '.ctl-size-option'
      );

    if (!sizeButton) return;

    const product =
      sizeButton.closest(
        '.ctl-product'
      );

    if (!product) return;

    const variantsElement =
      product.querySelector(
        '.ctl-variants-data'
      );

    if (!variantsElement) return;

    let variants;

    try {
      variants = JSON.parse(
        variantsElement.textContent
      );
    } catch (error) {
      console.error(
        'CTL variants JSON error:',
        error
      );

      return;
    }

    const selectedColorButton =
      product.querySelector(
        '.ctl-color-option.is-selected'
      );

    const selectedColor =
      selectedColorButton
        ? selectedColorButton.dataset
            .color
        : null;

    const selectedSize =
      sizeButton.dataset.size;

    let colorIndex = -1;

    if (selectedColor) {
      variants.some(
        function (variant) {
          return variant.options.some(
            function (
              option,
              index
            ) {
              if (
                option ===
                selectedColor
              ) {
                colorIndex =
                  index;

                return true;
              }

              return false;
            }
          );
        }
      );
    }

    const matchingVariant =
      variants.find(
        function (variant) {
          if (
            !variant.available ||
            !variant.options
          ) {
            return false;
          }

          const hasSize =
            variant.options.includes(
              selectedSize
            );

          const hasColor =
            !selectedColor ||
            (colorIndex !== -1 &&
              variant.options[
                colorIndex
              ] ===
                selectedColor);

          return (
            hasColor &&
            hasSize
          );
        }
      );

    if (!matchingVariant) {
      console.log(
        'No variant found:',
        selectedColor,
        selectedSize
      );

      return;
    }

    product
      .querySelectorAll(
        '.ctl-size-option'
      )
      .forEach(
        function (button) {
          button.classList.remove(
            'is-selected'
          );
        }
      );

    sizeButton.classList.add(
      'is-selected'
    );

    const sizeLabel =
      product.querySelector(
        '.ctl-selected-size'
      );

    if (sizeLabel) {
      sizeLabel.textContent =
        selectedSize;
    }

    const price =
      product.querySelector(
        '.ctl-product__price-current'
      );

    if (
      price &&
      matchingVariant.priceFormatted
    ) {
      price.textContent =
        matchingVariant.priceFormatted;
    }

    const image =
      product.querySelector(
        '.ctl-product__image'
      );

    if (
      image &&
      matchingVariant.featuredImage
    ) {
      image.style.opacity = '0';

      setTimeout(function () {
        image.src =
          matchingVariant.featuredImage;

        image.style.opacity = '1';
      }, 150);
    }

    product.dataset.selectedVariant =
      matchingVariant.id;

    console.log(
      'CTL Selected Variant:',
      matchingVariant.id
    );
  }
);

/*
 * =========================================================
 * COMPLETE THE LOOK - ADD TO CART
 * =========================================================
 */

document.addEventListener(
  'click',
  function (event) {
    const addButton =
      event.target.closest(
        '.ctl-add-to-cart'
      );

   if (!addButton) return;

event.preventDefault();
event.stopPropagation();

const product = addButton.closest(
  '.ctl-product'
);

    if (!product) return;

    const variantsElement =
      product.querySelector(
        '.ctl-variants-data'
      );

    if (!variantsElement) return;

    let variants;

    try {
      variants = JSON.parse(
        variantsElement.textContent
      );
    } catch (error) {
      console.error(
        'CTL variants JSON error:',
        error
      );

      return;
    }

    const selectedColorButton =
      product.querySelector(
        '.ctl-color-option.is-selected'
      );

    const selectedColor =
      selectedColorButton
        ? selectedColorButton.dataset
            .color
        : null;

    const selectedSizeButton =
      product.querySelector(
        '.ctl-size-option.is-selected'
      );

    const selectedSize =
      selectedSizeButton
        ? selectedSizeButton.dataset
            .size
        : null;

    let variantId =
      product.dataset.selectedVariant;

    if (!variantId) {
      const defaultVariant =
        variants.find(
          function (variant) {
            return (
              variant.available &&
              variant.options
            );
          }
        );

      if (defaultVariant) {
        variantId =
          defaultVariant.id;
      }
    }

    if (!variantId) {
      console.error(
        'CTL: No variant selected'
      );

      return;
    }

    const selectedVariant =
      variants.find(
        function (variant) {
          return (
            String(variant.id) ===
            String(variantId)
          );
        }
      );

    if (
      !selectedVariant ||
      !selectedVariant.available
    ) {
      console.error(
        'CTL: Selected variant is unavailable'
      );

      return;
    }

    const originalText =
      addButton.innerHTML;

    addButton.disabled = true;

    addButton.innerHTML =
      '<span>Adding...</span>';

    fetch(
      window.Shopify.routes.root +
        'cart/add.js',
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json',
        },

        body: JSON.stringify({
          items: [
            {
              id: selectedVariant.id,
              quantity: 1,
            },
          ],
        }),
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error(
            'Unable to add product to cart.'
          );
        }

        return response.json();
      })
      .then(function (data) {
        console.log(
          'CTL Added to Cart:',
          data
        );

        addButton.innerHTML =
          '<span>Added ✓</span>';

        if (
          typeof publish ===
            'function' &&
          typeof PUB_SUB_EVENTS !==
            'undefined'
        ) {
          publish(
            PUB_SUB_EVENTS.cartUpdate,
            {
              source:
                'complete-the-look',
              cartData: data,
            }
          );
        }

        setTimeout(
          function () {
            addButton.innerHTML =
              originalText;

            addButton.disabled =
              false;
          },
          1200
        );
      })
      .catch(function (error) {
        console.error(
          'CTL Add to Cart Error:',
          error
        );

        addButton.innerHTML =
          '<span>Try Again</span>';

        addButton.disabled =
          false;
      });
  }
);



/* =========================================================
   CUSTOM ADD TO CART BUTTON PRICE + STOCK
   Keeps price/compare-price after variant changes
   ========================================================= */

document.addEventListener('DOMContentLoaded', function () {

  const productFormButton = document.querySelector(
    'product-form .product-form__submit'
  );

  if (!productFormButton) return;

  const productForm = productFormButton.closest('product-form');

  if (!productForm) return;

  /*
   * Get product handle from current product URL
   */
  function getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?]+)/);
    return match ? match[1] : null;
  }

  const productHandle = getProductHandle();

  if (!productHandle) return;

  let productData = null;

  /*
   * Format Shopify money
   */
 function formatMoney(cents) {
  if (cents === null || cents === undefined) {
    return '';
  }

  cents = Number(cents);

  if (isNaN(cents)) {
    return '';
  }

  return '$' + (cents / 100).toFixed(2);
}

  /*
   * Get currently selected variant ID
   */
  function getSelectedVariantId() {

    const variantInput = document.querySelector(
      'product-form input[name="id"]'
    );

    if (variantInput && variantInput.value) {
      return String(variantInput.value);
    }

    const variantSelect = document.querySelector(
      'variant-selects'
    );

    if (variantSelect) {

      const selectedOption = variantSelect.querySelector(
        'select option[selected], fieldset input:checked'
      );

      if (selectedOption) {
        return selectedOption.dataset.variantId || null;
      }
    }

    return null;
  }

  /*
   * Find selected variant
   */
  function getSelectedVariant() {

    if (!productData || !productData.variants) {
      return null;
    }

    const variantId = getSelectedVariantId();

    if (!variantId) {
      return null;
    }

    return productData.variants.find(function (variant) {
      return String(variant.id) === String(variantId);
    }) || null;
  }

  /*
   * Build custom button content
   */
  function updateButtonContent() {

    const button = document.querySelector(
      'product-form .product-form__submit'
    );

    if (!button) return;

    const variant = getSelectedVariant();

    if (!variant) return;

    /*
     * Don't change disabled state here.
     * Dawn handles availability.
     */

    const price = formatMoney(variant.price);

    const compareAtPrice =
      variant.compare_at_price &&
      Number(variant.compare_at_price) > Number(variant.price)
        ? formatMoney(variant.compare_at_price)
        : null;

    /*
     * Stock text
     */
    let stockText = '';

    if (variant.available) {
      stockText = 'In-Stock: Ready to Ship';
    } else {
      stockText = 'Out of Stock';
    }

    /*
     * Create button HTML
     */
    let html = `
      <span class="atc-button-content">

        <span class="atc-main-line">
          Add to cart
    `;

    if (compareAtPrice) {

      html += `
          -
          <s class="atc-compare-price">
            ${compareAtPrice}
          </s>

          <strong class="atc-regular-price">
            ${price}
          </strong>
      `;

    } else {

      html += `
          -
          <strong class="atc-regular-price">
            ${price}
          </strong>
      `;

    }

    html += `
        </span>

        <span class="atc-stock-text">
          ${stockText}
        </span>

      </span>

      <div class="loading__spinner hidden">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="spinner"
          viewBox="0 0 66 66"
        >
          <circle
            stroke-width="6"
            cx="33"
            cy="33"
            r="30"
            fill="none"
            class="path"
          ></circle>
        </svg>
      </div>
    `;

    /*
     * Replace only button content.
     */
    button.innerHTML = html;

    /*
     * Make sure clicks on custom text/price always reach the real
     * Shopify Add to Cart button.
     */
    button.querySelectorAll('.atc-button-content, .atc-button-content *').forEach(function (element) {
      element.style.pointerEvents = 'none';
    });

    /*
     * Keep button availability synced with variant
     */
    if (variant.available) {
      button.removeAttribute('disabled');
    }
  }

  /*
   * Load product JSON
   */
  fetch(`/products/${productHandle}.js`)
    .then(function (response) {
      if (!response.ok) {
        throw new Error('Unable to load product data');
      }

      return response.json();
    })
    .then(function (data) {

      productData = data;

      /*
       * Initial load
       */
      setTimeout(function () {
        updateButtonContent();
      }, 100);

      setTimeout(function () {
        updateButtonContent();
      }, 500);

    })
    .catch(function (error) {
      console.error(
        'Custom ATC product data error:',
        error
      );
    });


  /*
   * IMPORTANT:
   *
   * Dawn changes/replaces the button when variant changes.
   * MutationObserver detects that and restores:
   *
   * Add to cart
   * Compare price
   * Regular price
   * Stock text
   */

  const buttonObserver = new MutationObserver(function () {

    setTimeout(function () {

      if (
        productData &&
        document.querySelector(
          'product-form .product-form__submit'
        )
      ) {
        updateButtonContent();
      }

    }, 50);

  });

  buttonObserver.observe(
    productForm,
    {
      childList: true,
      subtree: true
    }
  );


  /*
   * Variant change listener
   */
  document.addEventListener('change', function (event) {

    if (
      event.target.matches(
        'variant-selects input, variant-selects select'
      )
    ) {

      /*
       * Give Dawn time to update hidden variant ID
       */
      setTimeout(function () {
        updateButtonContent();
      }, 100);

      setTimeout(function () {
        updateButtonContent();
      }, 300);

      setTimeout(function () {
        updateButtonContent();
      }, 700);

    }

  });

});





// bottom sticky

document.addEventListener('DOMContentLoaded', function () {

  const stickyBar = document.getElementById('sticky-product-bar');

  if (!stickyBar) return;

  function handleStickyBar() {

    if (window.scrollY > 300) {
      stickyBar.classList.add('is-visible');
    } else if (window.scrollY <= 100) {
      stickyBar.classList.remove('is-visible');
    }

  }

  window.addEventListener('scroll', handleStickyBar, {
    passive: true
  });

});
